import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '~/api';
import { useStoreId, useStoreUser } from '~/providers/StoreUserProvider';
import { ThemeSettings } from '~/types';
import { isErrorMessage } from '~/utils/errorResponse';
import { useToast } from './useToast';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['themeSettings', storeId] as const;
};

export const useThemeSettings = () => {
  const queryKey = useQueryKey();
  const {
    store: { customerAppUrl },
  } = useStoreUser();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) =>
      api
        .store(storeId)
        .settings.theme.get()
        .then((data) => ({
          ...data,
          customerAppUrl,
        })),
  });
};

export const useThemeSettingsUpdate = () => {
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<ThemeSettings>) => {
      const theme = queryClient.getQueryData<ThemeSettings>(queryKey);

      if (!theme) {
        throw new Error('Theme settings not found');
      }

      return api.store(storeId).settings.theme.update({ ...theme, ...data });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      toast.show(message);
      return error;
    },
  });
};
